import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from './Layout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { mockDeals } from '../data/mockData';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Trash2,
  DollarSign,
  Calendar,
  User,
  TrendingUp,
  Target
} from 'lucide-react';

const Deals = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState(mockDeals);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  const filteredDeals = deals.filter(deal =>
    deal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deal.account.toLowerCase().includes(searchTerm.toLowerCase()) ||
    deal.owner.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Qualification': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Proposal': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Negotiation': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Closed Won': return 'bg-green-100 text-green-800 border-green-200';
      case 'Closed Lost': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProbabilityValue = (probability) => {
    return parseInt(probability.replace('%', ''));
  };

  const handleDeleteDeal = (dealId) => {
    if (window.confirm('Are you sure you want to delete this deal?')) {
      setDeals(deals.filter(deal => deal.id !== dealId));
    }
  };

  const totalValue = filteredDeals.reduce((sum, deal) => {
    return sum + parseFloat(deal.value.replace(/[\$,]/g, ''));
  }, 0);

  const avgProbability = filteredDeals.reduce((sum, deal) => {
    return sum + getProbabilityValue(deal.probability);
  }, 0) / filteredDeals.length || 0;

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Deals</h1>
            <p className="text-gray-600 mt-1">Track and manage your sales opportunities</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Deal
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Pipeline</p>
                  <p className="text-2xl font-bold text-gray-900">${totalValue.toLocaleString()}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Deals</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredDeals.length}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Avg. Probability</p>
                  <p className="text-2xl font-bold text-gray-900">{avgProbability.toFixed(0)}%</p>
                </div>
                <Target className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="search"
              placeholder="Search deals by name, account, or owner..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
        </div>

        {/* Deals Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredDeals.map((deal) => (
            <Card key={deal.id} className="hover:shadow-lg transition-shadow duration-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-gray-900 truncate">
                    {deal.name}
                  </CardTitle>
                  <Badge className={getStageColor(deal.stage)}>
                    {deal.stage}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600">{deal.account}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Deal Value</span>
                    <span className="text-lg font-bold text-gray-900">{deal.value}</span>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Probability</span>
                      <span className="font-medium">{deal.probability}</span>
                    </div>
                    <Progress 
                      value={getProbabilityValue(deal.probability)} 
                      className="h-2"
                    />
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    Expected Close: {deal.expectedClose}
                  </div>

                  <div className="flex items-center text-sm text-gray-600">
                    <User className="h-4 w-4 mr-2" />
                    Owner: {deal.owner}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Last Activity: {deal.lastActivity}
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedDeal(deal)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteDeal(deal.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredDeals.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <div className="text-gray-500">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No deals found</h3>
                <p className="text-sm">Try adjusting your search criteria or add a new deal.</p>
                <Button className="mt-4 bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Deal
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deal Detail Modal */}
        {selectedDeal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Deal Details</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedDeal(null)}
                >
                  ×
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">{selectedDeal.name}</h2>
                  <Badge className={getStageColor(selectedDeal.stage)}>
                    {selectedDeal.stage}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Account</label>
                    <p className="text-gray-900 mt-1">{selectedDeal.account}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Deal Owner</label>
                    <p className="text-gray-900 mt-1">{selectedDeal.owner}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Deal Value</label>
                    <p className="text-gray-900 mt-1 text-lg font-semibold">{selectedDeal.value}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Probability</label>
                    <div className="mt-1">
                      <p className="text-gray-900 font-medium">{selectedDeal.probability}</p>
                      <Progress 
                        value={getProbabilityValue(selectedDeal.probability)} 
                        className="h-2 mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Expected Close Date</label>
                    <p className="text-gray-900 mt-1">{selectedDeal.expectedClose}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Last Activity</label>
                    <p className="text-gray-900 mt-1">{selectedDeal.lastActivity}</p>
                  </div>
                </div>

                <div className="flex space-x-2 pt-4 border-t">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Move Stage
                  </Button>
                  <Button variant="outline">
                    Add Activity
                  </Button>
                  <Button variant="outline">
                    Send Proposal
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Deals;